import { uniq } from 'ramda';
import type { Location } from 'slate';
import { Editor, Point, Range, Text, Transforms } from 'slate';

import type { UUID } from '../../types/type-helpers';
import type { AlineText, EditingMode } from '../internal';
import { AlineTransforms, Queries, SelEventSource } from '../internal';

const CommentTransforms = {
  deleteComment: (
    editor: Editor,
    options: { editingMode: EditingMode; nodeRefId: string }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { nodeRefId } = options;
      const textNodeEntries = Editor.nodes<AlineText>(editor, {
        at: [],
        match: node => {
          return (
            Text.isText(node) && Array.isArray(node.comments) && node.comments.includes(nodeRefId)
          );
        },
        voids: true,
      });

      for (const [textNode, textPath] of textNodeEntries) {
        const newComments = (textNode.comments ?? []).filter((id: string) => id !== nodeRefId);
        Transforms.setNodes(editor, { comments: newComments }, { at: textPath, voids: true });
      }
    });
  },
  deleteComments: (
    editor: Editor,
    options: { editingMode: EditingMode; nodeRefIds: string[] }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { editingMode, nodeRefIds } = options;

      for (const nodeRefId of nodeRefIds) {
        AlineTransforms.deleteComment(editor, { editingMode, nodeRefId });
      }
    });
  },
  insertComment: (
    editor: Editor,
    options: { editingMode: EditingMode; nodeRefId: string; at?: Range }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, editingMode, nodeRefId } = options;
      if (!at) return;

      if (Queries.getGridSelectionInfoForLocation(editor, { at })) {
        for (const [, cellPath] of Queries.getGridSelectionCellEntries(editor) ?? [])
          commentTextInRange(editor, {
            at: Editor.range(editor, cellPath),
            editingMode,
            nodeRefId,
          });
        return;
      }

      const newRange: Range = { anchor: at.anchor, focus: at.focus };

      /**
       * If the Range is collapsed, we push the range forward a word and backward a word,
       * unless the cursor is at the beginning or end of a paragraph.
       */
      if (Range.isCollapsed(newRange)) {
        const paragraphEntry = Queries.paragraphAtPoint(editor, { at: newRange.anchor });
        if (!paragraphEntry) return;

        const [, paragraphPath] = paragraphEntry;
        const [start, end] = Editor.edges(editor, paragraphPath);
        const cursorAtStartOfParagraph = Point.equals(newRange.anchor, start);
        const cursorAtEndOfParagraph = Point.equals(newRange.anchor, end);
        const pointAtWordBefore = Editor.before(editor, newRange.anchor, {
          distance: 1,
          unit: 'word',
          voids: true,
        });
        const pointAtWordAfter = Editor.after(editor, newRange.anchor, {
          distance: 1,
          unit: 'word',
          voids: true,
        });

        /**
         *  If the cursor isn't at the start of a paragraph, and Slate can resolve the point before the cursor,
         *  we set the anchor to the point at the word before.  If the cursor is collapsed in the middle of a word,
         *  the point is the beginning of that word.  If the cursor is in the middle of words (e.g., whitespace),
         *  the point becomes the word before the whitespace.
         */
        if (!cursorAtStartOfParagraph && pointAtWordBefore) newRange.anchor = pointAtWordBefore;

        /**
         *  If the cursor isn't at the end of a paragraph, and Slate can resolve the point after the cursor,
         *  we set the focus to the point at the word after.  If the cursor is collapsed in the middle of a word,
         *  the point is the end of that word.  If the cursor is in the middle of words (e.g., whitespace),
         *  the point becomes the word after the whitespace.
         */
        if (!cursorAtEndOfParagraph && pointAtWordAfter) newRange.focus = pointAtWordAfter;
      }

      commentTextInRange(editor, { at: newRange, editingMode, nodeRefId });
    });
  },
  selectComment: (
    editor: Editor,
    options: { commentId: UUID; select?: 'start' | 'entire-comment' }
  ): Location | null => {
    return AlineTransforms.withoutNormalizing<Location | null>(editor, () => {
      const { commentId, select = 'start' } = options;
      const textNodeEntries = [
        ...Editor.nodes<AlineText>(editor, {
          at: [],
          match: node =>
            Text.isText(node) && Array.isArray(node.comments) && node.comments.includes(commentId),
          voids: true,
        }),
      ];
      if (!textNodeEntries.length) return null;

      const [[, firstTextPath]] = textNodeEntries;
      const lastIndex = Math.max(0, textNodeEntries.length - 1);
      const [, lastTextPath] = textNodeEntries[lastIndex];

      switch (select) {
        case 'start': {
          const startComment = Editor.start(editor, firstTextPath);

          AlineTransforms.select(editor, startComment, {
            eventSource: SelEventSource.PROGRAMMATIC,
          });
          return startComment;
        }
        case 'entire-comment': {
          const startComment = Editor.start(editor, firstTextPath);
          const endComment = Editor.end(editor, lastTextPath);
          const commentRange: Range = { anchor: startComment, focus: endComment };

          AlineTransforms.select(editor, commentRange, {
            eventSource: SelEventSource.PROGRAMMATIC,
          });
          return commentRange;
        }
      }
    });
  },
};

export default CommentTransforms;

/**
 * Split the text nodes at the range's edges, then stamp the comment id on every text node inside it.
 */
function commentTextInRange(
  editor: Editor,
  options: { at: Range; editingMode: EditingMode; nodeRefId: string }
): void {
  const { at, editingMode, nodeRefId } = options;

  const rangeAfterSplit = AlineTransforms.splitTextAtEdges(editor, {
    at,
    editingMode,
    voids: true,
  });
  if (!rangeAfterSplit) return;

  for (const [textNode, path] of Editor.nodes<AlineText>(editor, {
    at: rangeAfterSplit,
    match: Text.isText,
    voids: true,
  })) {
    const uniqCommentIds = uniq([...(textNode.comments ?? []), nodeRefId]);
    Transforms.setNodes(editor, { comments: uniqCommentIds }, { at: path, voids: true });
  }
}
